import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Truck, Brain, BarChart, Clock,
  TrendingUp, MapPin, Package, Zap
} from 'lucide-react';

const AIConsultingLogistics = () => {
  const useCases = [
    {
      icon: MapPin,
      title: 'Route Optimization AI',
      description: 'ML-powered routing that considers real-time traffic, delivery windows, vehicle capacity, and fuel cost dynamically updated as conditions change.',
      outcomes: ['15-25% reduction in fuel costs', 'On-time delivery improvement by 20%+', 'Real-time route adjustment'],
    },
    {
      icon: Package,
      title: 'Demand Forecasting & Inventory AI',
      description: 'Time-series ML models that predict demand at the SKU/location level, reducing stockouts and overstock across the supply chain.',
      outcomes: ['30-50% reduction in excess inventory', '90%+ forecast accuracy at 30-day horizon', 'Automated replenishment triggers'],
    },
    {
      icon: Brain,
      title: 'Predictive Maintenance for Fleet',
      description: 'IoT-connected ML models that predict vehicle and equipment failures before they happen, reducing unplanned downtime and repair costs.',
      outcomes: ['40% reduction in unplanned breakdowns', 'Predictive alerts 2-4 weeks in advance', 'Maintenance cost reduction 20-30%'],
    },
    {
      icon: Truck,
      title: 'Shipment Tracking & Visibility AI',
      description: 'AI-powered ETA prediction, exception detection, and proactive customer communication systems for freight and last-mile delivery.',
      outcomes: ['Accurate ETAs within ±2 hour window', 'Automated exception alerts to customers', 'Carrier performance benchmarking'],
    },
    {
      icon: BarChart,
      title: 'Warehouse Operations AI',
      description: 'Computer vision and ML for pick path optimization, labor allocation forecasting, and automated quality inspection at receiving and dispatch.',
      outcomes: ['25% improvement in pick efficiency', 'Automated receiving with computer vision', 'Labor demand forecasting by shift'],
    },
    {
      icon: TrendingUp,
      title: 'Freight Pricing & Revenue Optimization',
      description: 'Dynamic pricing ML that optimizes spot and contract freight rates based on market conditions, capacity utilization, and historical lane data.',
      outcomes: ['5-15% revenue improvement per lane', 'Automated rate recommendation at quote', 'Competitive lane intelligence'],
    },
  ];

  return (
    <>
      <Helmet>
        <title>AI Consulting for Logistics | Supply Chain AI & Route Optimization | Agility</title>
        <meta
          name="description"
          content="Logistics AI consulting specialists. Route optimization, demand forecasting, fleet predictive maintenance, warehouse AI, and freight pricing ML. Production AI in 3-8 weeks for 3PLs, carriers, e-commerce fulfillment, and supply chain companies."
        />
        <meta
          name="keywords"
          content="AI consulting logistics, logistics AI consulting, supply chain AI, route optimization AI, demand forecasting AI, fleet AI, warehouse AI, freight AI consulting, 3PL AI, supply chain machine learning"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-consulting-logistics" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Consulting for Logistics | Supply Chain & Route Optimization AI | Agility" />
        <meta property="og:description" content="Logistics AI consulting: route optimization, demand forecasting, fleet maintenance AI, warehouse automation. Production in 3-8 weeks." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-consulting-logistics" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Logistics AI Consulting",
            "provider": { "@type": "Organization", "name": "Agility", "url": "https://www.agilitytech.ai" },
            "description": "AI consulting for logistics and supply chain companies. Route optimization ML, demand forecasting, fleet maintenance prediction, warehouse AI, and freight pricing optimization.",
            "areaServed": "Worldwide",
            "serviceType": "Logistics AI Consulting"
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-900 via-black to-teal-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-8">
            <Truck className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">Logistics & Supply Chain AI Consulting</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Consulting for<br />
            <span className="text-teal-400">Logistics & Supply Chain</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Route optimization, demand forecasting, fleet AI, and warehouse intelligence for 3PLs, carriers,
            e-commerce fulfillment, and supply chain operators. Production-ready in 3-8 weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Get a Logistics AI Consultation
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              View Case Studies
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { value: '3-8 Weeks', label: 'To Production', desc: 'From discovery to live system' },
              { value: '25%', label: 'Fuel Savings', desc: 'Route optimization AI avg.' },
              { value: '40%', label: 'Less Downtime', desc: 'Predictive fleet maintenance' },
              { value: '90%+', label: 'Forecast Accuracy', desc: 'Demand forecasting models' },
            ].map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1">{m.label}</div>
                <div className="text-gray-400 text-sm">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Logistics AI Use Cases We Deliver</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specific ML implementations that move the needle on margin, capacity, and service level.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((u, i) => {
              const Icon = u.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{u.title}</h3>
                  <p className="text-gray-600 text-sm mb-5 leading-relaxed">{u.description}</p>
                  <ul className="space-y-2">
                    {u.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related links */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'AI Implementation Services', href: '/ai-implementation-services' },
              { label: 'Generative AI Consulting', href: '/generative-ai-consulting' },
              { label: 'AI for Manufacturing', href: '/case-studies/manufacturing-ai' },
              { label: 'AI for Insurance', href: '/ai-consulting-insurance' },
              { label: 'Data Engineering Solutions', href: '/solutions/data-engineering' },
              { label: 'AI Consulting India', href: '/ai-consulting-india' },
              { label: 'Retail Analytics Case Study', href: '/case-studies/retail-analytics' },
              { label: 'All Case Studies', href: '/case-studies' },
            ].map((link, i) => (
              <Link key={i} to={link.href} className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm group">
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Optimize Your Supply Chain with AI?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us your route, demand, or warehouse challenge. We'll scope an AI solution and give you a timeline in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Talk to a Logistics AI Consultant
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default AIConsultingLogistics;
